import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation } from 'convex/react'
import { useEffect, useState } from 'react'

import { InboxGoalView } from '@/components/inbox-goal-view'
import { useGoals } from '@/hooks/use-goals'
import type { InboxTab } from '@/store/view.store'

export const Route = createFileRoute('/_authenticated/everyday')({
  head: () => ({
    meta: [{ title: 'Everyday — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  validateSearch: (
    search,
  ): {
    addTask?: boolean
    compose?: 'event'
    focusTask?: string
    focusEvent?: string
    openTask?: string
    tab?: InboxTab
    due?: 'today'
  } => ({
    addTask: search.addTask === true || search.addTask === 'true' ? true : undefined,
    compose: search.compose === 'event' ? 'event' : undefined,
    focusTask: typeof search.focusTask === 'string' ? search.focusTask : undefined,
    focusEvent: typeof search.focusEvent === 'string' ? search.focusEvent : undefined,
    openTask: typeof search.openTask === 'string' ? search.openTask : undefined,
    tab:
      search.tab === 'habits' || search.tab === 'tasks' || search.tab === 'events'
        ? search.tab
        : undefined,
    due: search.due === 'today' ? 'today' : undefined,
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const goals = useGoals()
  const navigate = useNavigate()
  const { addTask, compose, focusTask, focusEvent, openTask, tab, due } = Route.useSearch()
  const inboxGoal = goals?.find((g) => g.isInbox)
  const ensureInbox = useMutation(api.nodes.ensureInbox)
  const [createdInboxId, setCreatedInboxId] = useState<Id<'nodes'> | null>(null)

  useEffect(() => {
    if (goals !== undefined && !inboxGoal && !createdInboxId) {
      ensureInbox()
        .then(({ id }) => setCreatedInboxId(id as Id<'nodes'>))
        .catch(() => {})
    }
  }, [goals, inboxGoal, createdInboxId])

  const goalId = (inboxGoal?._id ?? createdInboxId) as Id<'nodes'> | null

  if (!goalId) {
    return (
      <div className="flex h-svh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    )
  }

  return (
    <InboxGoalView
      goalId={goalId}
      focusInput={addTask}
      composeEvent={compose === 'event'}
      composerDueToday={addTask && due === 'today'}
      focusTaskId={focusTask}
      focusEventId={focusEvent}
      openTaskId={openTask}
      initialTab={compose === 'event' || focusEvent ? 'events' : tab}
      onFocusHandled={() =>
        navigate({
          from: '/everyday',
          to: '/everyday',
          search: (prev) => ({
            ...prev,
            focusTask: undefined,
            focusEvent: undefined,
            openTask: undefined,
          }),
          replace: true,
        })
      }
    />
  )
}
